import React, { useContext, useEffect, useState } from "react";
import NEUTRAL_ICON from "../../assets/elections/review/neutral@3x.png";
import RATHER_AGREE_ICON from "../../assets/elections/review/ratherAgree@3x.png";
import RATHER_DISAGREE_ICON from "../../assets/elections/review/ratherDisagree@3x.png";
import AGREE_ICON from "../../assets/slider/agree@3x.png";
import DISAGREE_ICON from "../../assets/slider/disagree@3x.png";
import Slider from "../../components/common/Slider";
import Spinner from "../../components/common/Spinner";
import ElectionButton from "../../components/common/VotingButtons/ElectionButton";
import CandidateProfile from "../../components/Election/CandidateProfile";
import ElectionResults from "../../components/Election/ElectionResults";
import KantonSelection from "../../components/Election/KantonSelection";
import Progress from "../../components/Progress";
import { I18nContext } from "../../providers/i18n";
import "./styles.scss";

const Election = ({
  savedResults,
  getElection,
  sendElectionResults,
  saveAnswers,
  election,
  results
}) => {
  const { t } = useContext(I18nContext);
  useEffect(getElection, []);

  const [answers, setAnswer] = useState({});
  const [currentIndex, setIndex] = useState(0);
  const [currentRegion, setRegion] = useState(null);
  const [currentCandidate, setCandidate] = useState(null);
  const hasResults = !!savedResults;

  useEffect(() => {
    election &&
      election.questions.length <= currentIndex &&
      handleLastAnswered();
  }, [currentIndex]);

  useEffect(() => {
    hasResults && setAnswer(savedResults);
  }, [hasResults]);

  if (!election) {
    return <ElectionLoading />;
  }

  const { questions, districts, questionsHash, district_label } = election;
  const question = questions[currentIndex];

  const answer = (answer, questionId) => {
    setIndex(prevIndex => prevIndex + 1);
    setAnswer(prevAnswers => ({ ...prevAnswers, [questionId]: answer }));
  };

  const handleLastAnswered = () => {
    saveAnswers(questionsHash, answers);
  };

  const onRegionChosen = region => {
    setRegion(region);
    sendElectionResults(region, answers);
  };

  const shouldShowKantonSelection =
    !currentRegion && (election.questions.length <= currentIndex || hasResults);
  const shouldShowResults = currentRegion;
  const progressPercentage = (100 / questions.length) * (currentIndex + 1);
  let toRender;

  const returnToList = () => {
    setCandidate(null);
  };

  const returnToRegionChooser = () => {
    setRegion(null);
  };

  if (shouldShowKantonSelection) {
    toRender = (
      <KantonSelection
        districtLabel={district_label}
        hasResults={hasResults}
        regions={districts}
        onRegionChosen={onRegionChosen}
      />
    );
  } else if (!!currentCandidate) {
    toRender = (
      <CandidateProfile
        candidate={currentCandidate}
        chartData={results.chartData}
        onBackButton={returnToList}
      />
    );
  } else if (shouldShowResults) {
    toRender = (
      <ElectionResults
        hash={questionsHash}
        hasResults={hasResults}
        onCandidateChosen={setCandidate}
        results={results}
        districtLabel={district_label}
        onBackButton={returnToRegionChooser}
      />
    );
  } else {
    toRender = (
      <>
        <h3 className="title">{question.categoryName}</h3>
        <div className="question">{question.name}</div>
        {question.type === "standard" && renderButtons(question.id, answer, t)}
        {question.type === "slider" && (
          <ElectionSlider answer={answer} questionId={question.id} t={t} />
        )}
        <Progress
          questionsNumber={questions.length}
          percentage={progressPercentage}
          currentIndex={currentIndex}
        />
      </>
    );
  }

  return (
    <div className="election">
      <div className="election-content">{toRender}</div>
    </div>
  );
};

const ElectionLoading = () => (
  <div style={{ width: "30%", margin: "auto" }}>
    <Spinner />
  </div>
);

const renderButtons = (questionId, answer, t) => {
  return (
    <div className="control">
      <ElectionButton
        size={90}
        bottomOffset={90}
        src={DISAGREE_ICON}
        onClick={() => answer(0, questionId)}
        label={t("elections.questionnaire.no")}
      />
      <ElectionButton
        size={60}
        bottomOffset={60}
        className="control-button"
        src={RATHER_DISAGREE_ICON}
        onClick={() => answer(25, questionId)}
        label={t("elections.questionnaire.ratherNo")}
      />
      <ElectionButton
        size={50}
        className="control-button"
        src={NEUTRAL_ICON}
        onClick={() => answer(-1, questionId)}
        label={t("elections.questionnaire.iDontKnow")}
      />
      <ElectionButton
        size={60}
        bottomOffset={60}
        className="control-button"
        src={RATHER_AGREE_ICON}
        onClick={() => answer(75, questionId)}
        label={t("elections.questionnaire.ratherYes")}
      />
      <ElectionButton
        size={90}
        bottomOffset={90}
        className="control-button"
        src={AGREE_ICON}
        onClick={() => answer(100, questionId)}
        label={t("elections.questionnaire.yes")}
      />
    </div>
  );
};

const colors = {
  0: "rgb(236,10,113)",
  50: "rgb(240,77,149)",
  100: "rgb(240,77,149)",
  150: "rgb(215, 215, 215)",
  200: "rgb(68,192,241)",
  250: "rgb(68,192,241)",
  300: "rgb(0,172,239)"
};

const ElectionSlider = ({ questionId, answer, t }) => {
  const [value, setValue] = useState(150);
  return (
    <div className={"slider-container"}>
      <div className={"slider-container__labels"}>
        <span className={"negative"}>{t("elections.slider.negative")}</span>
        <span className={"neutral"}>
          {t("elections.questionnaire.iDontKnow")}
        </span>
        <span className={"positive"}>{t("elections.slider.positive")}</span>
      </div>
      <div className="scale-container">
        <div className="scale-item">|</div>
        <div className="scale-item">|</div>
        <div className="scale-item">|</div>
        <div className="scale-item">|</div>
        <div className="scale-item">|</div>
        <div className="scale-item">|</div>
        <div className="scale-item">|</div>
      </div>
      <Slider
        onRelease={() => {
          answer(value / 3, questionId);
          setValue(150);
        }}
        style={{ color: colors[150] }}
        min={0}
        max={300}
        step={50}
        value={value}
        onChange={e => setValue(e.target.value)}
      />
    </div>
  );
};

export default Election;
